/**
 * AppICEMAP — Image Utility
 * Client-side photo resize and Directus upload helper.
 *
 * - resizeImage(file, maxWidth): resize via Canvas API before upload
 * - uploadPhoto(file): upload to Directus Files, return file UUID
 */

import { DIRECTUS_URL, getClient, uploadFiles } from "../api/directus.js";

/** Formatos que el navegador sabe decodificar y Directus almacena sin drama */
export const SUPPORTED_IMAGE_TYPES = [
	"image/jpeg",
	"image/png",
	"image/webp",
	"image/gif",
];

/** Tamaño máximo aceptado antes de redimensionar */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

/**
 * Comprobar que el archivo elegido es una imagen utilizable.
 *
 * El caso importante es HEIC/HEIF: es el formato por defecto del iPhone y la
 * mayoría de navegadores NO puede decodificarlo, así que el canvas fallaría más
 * tarde con un error opaco. Mejor decirlo al elegir la foto.
 *
 * @param {File} file
 * @returns {{ ok: boolean, reason?: string }}
 */
export function validateImageFile(file) {
	if (!file) return { ok: false, reason: "No se seleccionó ninguna foto." };

	const name = (file.name || "").toLowerCase();
	const type = (file.type || "").toLowerCase();

	const isHeic =
		type.includes("heic") ||
		type.includes("heif") ||
		name.endsWith(".heic") ||
		name.endsWith(".heif");

	if (isHeic) {
		return {
			ok: false,
			reason:
				"Las fotos HEIC del iPhone no se pueden subir. En Ajustes → Cámara → Formatos, elige «Más compatible», o comparte la foto como JPG.",
		};
	}

	// Algunos navegadores no rellenan el type: nos guiamos por la extensión
	const knownExtension = /\.(jpe?g|png|webp|gif)$/i.test(name);
	if (type && !SUPPORTED_IMAGE_TYPES.includes(type)) {
		return {
			ok: false,
			reason: `Ese tipo de archivo (${type}) no es compatible. Usa JPG, PNG, WEBP o GIF.`,
		};
	}
	if (!type && !knownExtension) {
		return {
			ok: false,
			reason: "No reconocemos ese archivo como imagen. Usa JPG, PNG, WEBP o GIF.",
		};
	}

	if (file.size > MAX_UPLOAD_BYTES) {
		const mb = Math.round(file.size / (1024 * 1024));
		return {
			ok: false,
			reason: `La foto pesa ${mb} MB y el máximo son 15 MB.`,
		};
	}

	return { ok: true };
}

/**
 * Resize an image file on the client side to reduce upload bandwidth.
 * Uses Canvas API. Strips EXIF by re-encoding.
 *
 * @param {File} file - Original image file
 * @param {number} [maxWidth=1920] - Maximum width in pixels (height scales proportionally)
 * @returns {Promise<Blob>} Resized image as a Blob (JPEG format)
 */
export function resizeImage(file, maxWidth = 1920) {
	return new Promise((resolve, reject) => {
		const check = validateImageFile(file);
		if (!check.ok) {
			reject(new Error(check.reason));
			return;
		}

		const img = new Image();
		const url = URL.createObjectURL(file);

		img.onload = () => {
			URL.revokeObjectURL(url);

			// Skip resize if already within limits
			if (img.width <= maxWidth) {
				// Still re-encode to strip EXIF
				const canvas = document.createElement("canvas");
				canvas.width = img.width;
				canvas.height = img.height;
				const ctx = canvas.getContext("2d");
				ctx.drawImage(img, 0, 0);

				canvas.toBlob(
					(blob) => {
						if (blob) resolve(blob);
						else reject(new Error("Failed to encode image"));
					},
					"image/jpeg",
					0.85,
				);
				return;
			}

			// Calculate new dimensions
			const ratio = maxWidth / img.width;
			const newWidth = maxWidth;
			const newHeight = Math.round(img.height * ratio);

			const canvas = document.createElement("canvas");
			canvas.width = newWidth;
			canvas.height = newHeight;

			const ctx = canvas.getContext("2d");
			// Use better image scaling
			ctx.imageSmoothingEnabled = true;
			ctx.imageSmoothingQuality = "high";
			ctx.drawImage(img, 0, 0, newWidth, newHeight);

			canvas.toBlob(
				(blob) => {
					if (blob) resolve(blob);
					else reject(new Error("Failed to encode image"));
				},
				"image/jpeg",
				0.85,
			);
		};

		img.onerror = () => {
			URL.revokeObjectURL(url);
			// Llegar aquí con un tipo admitido suele significar un archivo
			// corrupto o un formato que este navegador no decodifica.
			reject(
				new Error(
					"No se pudo leer la imagen. Puede estar dañada o en un formato que tu navegador no admite.",
				),
			);
		};

		img.src = url;
	});
}

/**
 * Upload a photo file to Directus Files.
 * Resizes client-side first, then POSTs.
 *
 * @param {File} file - The photo file
 * @returns {Promise<string>} UUID of the uploaded file
 */
export async function uploadPhoto(file) {
	// Resize first
	const resized = await resizeImage(file, 1920);

	// Create a new File-like form data entry
	const formData = new FormData();
	formData.append("file", resized, file.name || "photo.jpg");

	const client = getClient();

	try {
		const result = await client.request(uploadFiles(formData));
		// Result is the Directus file object
		if (result && result.id) {
			return result.id;
		}
		// uploadFiles may return array
		if (Array.isArray(result) && result[0]?.id) {
			return result[0].id;
		}
		throw new Error("Unexpected upload response");
	} catch (err) {
		console.error("[Image] Upload failed:", err);
		throw err;
	}
}

/**
 * Get the Directus file URL for a file UUID.
 * @param {string} fileId - UUID of the Directus file
 * @returns {string}
 */
export function getFileUrl(fileId) {
	return `${DIRECTUS_URL}/assets/${fileId}`;
}
